import _styled, { default as _styled2 } from "styled-components";
const _excluded = ["style", "className", "children", "name", "image", "imageUrl", "color", "size"];
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var n = Object.getOwnPropertySymbols(e); for (r = 0; r < n.length; r++) o = n[r], -1 === t.indexOf(o) && {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (-1 !== e.indexOf(n)) continue; t[n] = r[n]; } return t; }
import React, { useContext, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { variantSwitch } from '@adsk/alloy-react-helpers';
import theme, { stylePropType } from '@adsk/alloy-react-theme';
import AvatarGroupContext from './AvatarGroupContext';
import { AVATAR_COLORS, AVATAR_SIZES, DEFAULT_AVATAR_NAME, DEFAULT_AVATAR_SUFFIX, REMOVED_USER_SUFFIX } from './constants';
import getColorByName from './getColorByName';
import initialsFromName from './initialsFromName';
const defaultVariants = {
  [AVATAR_SIZES.SMALL_16]: {
    width: '16px',
    height: '16px',
    fontSize: '11px'
  },
  [AVATAR_SIZES.MEDIUM_24]: {
    width: '24px',
    height: '24px',
    fontSize: '12px'
  },
  [AVATAR_SIZES.MEDIUM_32]: {
    width: '32px',
    height: '32px',
    fontSize: '16px'
  },
  [AVATAR_SIZES.LARGE_48]: {
    width: '48px',
    height: '48px',
    fontSize: '24px'
  },
  [AVATAR_SIZES.XLARGE_64]: {
    width: '64px',
    height: '64px',
    fontSize: '32px'
  },
  [AVATAR_SIZES.XXLARGE_128]: {
    width: '128px',
    height: '128px',
    fontSize: '56px'
  }
};
const groupVariants = {
  [AVATAR_SIZES.SMALL_16]: {
    marginLeft: '-3px',
    border: `1px solid ${theme.tokens.colors.border.inverse.value}`
  },
  [AVATAR_SIZES.MEDIUM_24]: {
    marginLeft: '-6px',
    border: `1px solid ${theme.tokens.colors.border.inverse.value}`
  },
  [AVATAR_SIZES.MEDIUM_32]: {
    marginLeft: '-6px',
    border: `2px solid ${theme.tokens.colors.border.inverse.value}`
  },
  [AVATAR_SIZES.LARGE_48]: {
    marginLeft: '-7px',
    border: `2px solid ${theme.tokens.colors.border.inverse.value}`
  },
  [AVATAR_SIZES.XLARGE_64]: {
    marginLeft: '-10px',
    border: `4px solid ${theme.tokens.colors.border.inverse.value}`
  },
  [AVATAR_SIZES.XXLARGE_128]: {
    marginLeft: '-20px',
    border: `4px solid ${theme.tokens.colors.border.inverse.value}`
  }
};

/**
 * Avatars are a visual representation of a customers identity.
 * These may be small thumbnails as part of a menu or more prominent, standalone elements on account and management views.
 * Initials are dynamically generated from the provided name and always rendered. This allows the initials to serve as a placeholder while the image is loading. Read more about when and how to use the Avatar component on the internal wiki.
 */
const Avatar = /*#__PURE__*/React.forwardRef((_ref, ref) => {
  let {
      style,
      className,
      children,
      name = DEFAULT_AVATAR_NAME,
      image,
      imageUrl,
      color: colorProp,
      size: sizeProp = AVATAR_SIZES.MEDIUM_32
    } = _ref,
    props = _objectWithoutProperties(_ref, _excluded);
  const [error, setError] = useState(false); // Whether image failed to load
  const [loaded, setLoaded] = useState(false); // Whether image loaded successfully

  let finalImage = imageUrl !== null && imageUrl !== void 0 ? imageUrl : image; // Support both imageUrl and image same as we use in our api
  if (finalImage && DEFAULT_AVATAR_SUFFIX.test(finalImage)) {
    finalImage = undefined; // remove default Image and show letter avatar instead
  }
  const finalName = useMemo(() => {
    if (name && REMOVED_USER_SUFFIX.test(name)) {
      return name.replace(REMOVED_USER_SUFFIX, ''); // remove any suffix inside parenthesis at the end
    }
    return name;
  }, [name]);
  const color = colorProp !== null && colorProp !== void 0 ? colorProp : getColorByName(finalName);
  const avatarGroup = useContext(AvatarGroupContext);
  const size = avatarGroup ? avatarGroup.size : sizeProp;
  const nameInitials = useMemo(() => initialsFromName(finalName, size), [finalName, size]);
  let sizeStyles = _objectSpread({}, defaultVariants[size !== null && size !== void 0 ? size : AVATAR_SIZES.MEDIUM_32]);
  if (avatarGroup) {
    sizeStyles = _objectSpread(_objectSpread({}, sizeStyles), groupVariants[size !== null && size !== void 0 ? size : AVATAR_SIZES.MEDIUM_32]);
  }
  return /*#__PURE__*/React.createElement(_StyledSpan, _extends({}, props, {
    className: className,
    ref: ref,
    "data-testid": "Avatar",
    $_css: [{
      display: 'inline-flex',
      rounded: 'full',
      alignItems: 'center',
      flexShrink: 0,
      justifyContent: 'center',
      position: 'relative',
      borderRadius: theme.borderRadius.circle,
      verticalAlign: 'middle',
      cursor: 'default',
      color: theme.tokens.colors.text.primary.value
    }, variantSwitch({
      statement: color,
      variants: {
        [AVATAR_COLORS.COLOR_1]: {
          background: theme.tokens.colors.background.accent.one.subtle.value
        },
        [AVATAR_COLORS.COLOR_2]: {
          background: theme.tokens.colors.background.accent.two.value
        },
        [AVATAR_COLORS.COLOR_3]: {
          background: theme.tokens.colors.background.accent.two.subtle.value
        },
        [AVATAR_COLORS.COLOR_4]: {
          background: theme.tokens.colors.background.accent.four.subtle.value
        },
        [AVATAR_COLORS.COLOR_5]: {
          background: theme.tokens.colors.background.accent.three.subtle.value
        },
        [AVATAR_COLORS.COLOR_6]: {
          background: theme.tokens.colors.background.accent.five.subtler.value
        },
        [AVATAR_COLORS.COLOR_7]: {
          background: theme.tokens.colors.background.accent.five.subtle.value
        }
      }
    }), sizeStyles, (avatarGroup === null || avatarGroup === void 0 ? void 0 : avatarGroup.size) && {
      boxSizing: 'content-box'
    }, style]
  }), !error && finalImage && /*#__PURE__*/React.createElement(_StyledImg, {
    alt: finalName,
    src: finalImage,
    "data-testid": "Avatar-img",
    onLoad: () => setLoaded(true),
    onError: () => setError(true),
    $_css2: [_objectSpread({
      width: '100%',
      height: '100%',
      borderRadius: theme.borderRadius.circle,
      objectFit: 'cover'
    }, !loaded && {
      display: 'none'
    })]
  }), (error || !loaded || !finalImage) && (children || nameInitials && /*#__PURE__*/React.createElement("span", null, nameInitials)));
});
Avatar.displayName = 'Avatar';
Avatar.propTypes = {
  /** Avatar's content when image is missing */
  children: PropTypes.any,
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** The user full name for the avatar */
  name: PropTypes.string,
  /** Set the size of the avatar */
  size: PropTypes.oneOf(Object.values(AVATAR_SIZES)),
  /** Set the colors of the avatar */
  color: PropTypes.oneOf(Object.values(AVATAR_COLORS)),
  /** URL to a profile image */
  image: PropTypes.string,
  /** URL to a profile image */
  imageUrl: PropTypes.string
};
export default Object.assign(Avatar, {
  SIZES: AVATAR_SIZES,
  COLORS: AVATAR_COLORS
});
var _StyledSpan = _styled("span")`${p => p.$_css}`;
var _StyledImg = _styled2("img").withConfig({
  displayName: "Avatar___StyledImg",
  componentId: "sc-gve10u-0"
})(["", ""], p => p.$_css2);
//# sourceMappingURL=Avatar.js.map